import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, Lock, User, Briefcase, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", profession: "" });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  function fmtErr(detail) {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
    return "Något gick fel.";
  }

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Lösenordet måste vara minst 8 tecken.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Konto skapat!");
      nav("/forum");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="register-page">
      <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-mono">
        <span className="title-rule" />Community
      </div>
      <h1 className="font-serif text-4xl font-medium mb-2 tracking-tight">Skapa konto</h1>
      <p className="text-sm text-muted-foreground mb-7">Bli en del av forumet – ställ frågor och dela kunskap.</p>

      <Card className="p-6 bg-card">
        <form onSubmit={submit} className="space-y-3">
          <Field icon={Mail} label="E-post" type="email" value={form.email} onChange={upd("email")} required testid="reg-email" />
          <Field icon={Lock} label="Lösenord (minst 8 tecken)" type="password" value={form.password} onChange={upd("password")} required testid="reg-password" />
          <Field icon={User} label="Namn" value={form.name} onChange={upd("name")} required testid="reg-name" />
          <Field icon={Briefcase} label="Yrke / studierollI (valfritt)" value={form.profession} onChange={upd("profession")} placeholder="Ex: Naprapatstudent, Fysioterapeut" testid="reg-profession" />
          <Button type="submit" disabled={loading} className="w-full" data-testid="reg-submit">
            {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : <UserPlus className="size-4 mr-2" />}
            Skapa konto
          </Button>
        </form>
      </Card>

      <p className="text-sm text-muted-foreground mt-5 text-center">
        Har du redan konto? <Link to="/logga-in" className="text-primary hover:text-accent">Logga in här</Link>
      </p>
    </div>
  );
}

function Field({ icon: Icon, label, testid, ...props }) {
  return (
    <div>
      <label className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1 block">{label}</label>
      <div className="relative">
        <Icon className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" data-testid={testid} {...props} />
      </div>
    </div>
  );
}
